import { CoreButton, CoreCheckbox, CoreInput, CoreSelect } from '@components'
import { useCurrencies, useEditCryptoAddressQry, useMerchantQry } from 'mmfintech-backend-api'
import type { Currency, ResponseCallback, SimpleCallback } from 'mmfintech-backend-api/types'
import { tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useEffect } from 'react'

import './CryptoRecipientModal.scss'

interface Props {
  recipientId?: number
  currencyCode?: string
  foreignId?: string
  personal?: boolean
  onClose?: SimpleCallback
  onSuccess?: ResponseCallback
}

function CryptoRecipientModal(props: Props) {
  const { recipientId, currencyCode, foreignId, personal = false, onClose, onSuccess } = props

  const { currencyOptions } = useCurrencies()
  const { isMerchantCompany } = useMerchantQry()

  const { formValues, isTagRequired, error, fetching, handleSubmit } = useEditCryptoAddressQry({
    recipientId,
    currencyCode,
    settlement: false,
    foreignId: !personal ? foreignId : undefined,
    onClose,
    onSuccess
  })

  const handleClick = () => {
    formValues.setValue('personal', !formValues.getValue('personal'))
  }

  useEffect(() => {
    formValues.setValue('personal', personal)
  }, [personal])

  return (
    <div className='recipient-edit-wrapper' data-test='crypto-recipient-edit-modal'>
      <div className='recipient-edit-title'>{tr('FRONTEND.CUSTOMERS.EDIT.TITLE_PAYMENT', 'Payment information')}</div>
      <div className='recipient-edit-form'>
        <CoreInput
          type='text'
          data-test='account-name'
          label={tr('FRONTEND.RECIPIENTS.LABELS.ACCOUNT_NAME', 'Recipient name')}
          maxLength={100}
          {...formValues.registerInput('name')}
          autoComplete='off'
        />

        {currencyCode ? (
          <CoreInput
            type='text'
            data-test='address'
            label={tr('FRONTEND.RECIPIENTS.LABELS.CURRENCY', 'Currency')}
            {...formValues.registerInput('currency')}
            autoComplete='off'
            disabled
          />
        ) : (
          <CoreSelect
            type='currency'
            data-test='currency'
            label={tr('FRONTEND.ACCOUNTS.CREATE.CURRENCY_LABEL', 'Currency')}
            options={currencyOptions((c: Currency) => c.type === 'CRYPTO')}
            value={formValues.getValue('currency')}
            onChange={(_, val: any) => {
              formValues.setValue('currency', val.value)
              formValues.setRequired('tag', val.value === 'XRP')
            }}
          />
        )}

        <CoreInput
          type='text'
          data-test='address'
          label={tr('FRONTEND.RECIPIENTS.LABELS.CRYPTO_ADDRESS', 'Address')}
          {...formValues.registerInput('address')}
          autoComplete='off'
        />

        {isTagRequired(formValues.getValue('currency')) && (
          <CoreInput
            type='text'
            data-test='tag'
            label={tr('FRONTEND.RECIPIENTS.CRYPTO.TAG', 'Tag')}
            {...formValues.registerInput('tag')}
            autoComplete='off'
          />
        )}

        <CoreInput
          type='text'
          data-test='description'
          label={tr('FRONTEND.RECIPIENTS.LABELS.DESCRIPTION', 'Description')}
          maxLength={100}
          {...formValues.registerInput('description')}
          autoComplete='off'
        />

        {isMerchantCompany && (
          <CoreCheckbox
            className='form-checkbox'
            type='check'
            checked={Boolean(formValues.getValue('personal'))}
            disabled={formValues.getValue('foreignId') || personal}
            value={'personal'}
            onClick={handleClick}
            label={tr('FRONTEND.RECIPIENTS.LABELS.CRYPTO_PERSONAL_ACCOUNT', 'This is my personal account')}
            {...formValues.registerCheckbox('personal')}
            tooltip={{
              enabled: true,
              text: tr(
                'FRONTEND.RECIPIENTS.LABELS.CRYPTO_PERSONAL_ACCOUNT_TOOLTIP',
                'Personal accounts are your own external accounts and are only visible to you, administrators, and authorized departments within a company account.'
              ),
              place: 'bottom',
              padding: '3px',
              backgroundColor: 'black',
              textColor: 'white'
            }}
          />
        )}

        <ErrorDisplay error={error} />

        <div className='recipient-edit-actions'>
          <CoreButton
            type='button'
            data-test='save-button'
            variation='primary-black'
            size='large'
            text={tr('FRONTEND.COOKIE_CONSENT.SAVE', 'Save')}
            className='save-button'
            onClick={handleSubmit}
            isLoading={fetching}
            disabled={fetching}
            fullWidth
          />
        </div>
      </div>
    </div>
  )
}

export default CryptoRecipientModal
